import React, { useState, useMemo, useEffect } from 'react';
import { GlobalButton } from '../GlobalButton';
import { GlobalTextarea } from '../GlobalTextarea';
import { GlobalDropdown } from '../GlobalDropdown';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { RiskCard } from './data/RiskCard';
import styles from './RATab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { FilterBar } from '../FilterBar';
import { RiskRecord } from '../../types';
import { createRisk } from '../../services/domainEntities';
import GlobalEmptyState from '../GlobalEmptyState';
import GlobalSidebar from '../GlobalSidebar';
import { URSAutoComplete } from '../URSAutoComplete';
import { FSAutoComplete } from '../FSAutoComplete';
import { TestAutoComplete } from '../TestAutoComplete';
import { SOPAutoComplete } from '../SOPAutoComplete';
import TransformEntitiesSidebar from '../TransformEntitiesSidebar';
import EntityGenerationSidebar from '../EntityGenerationSidebar';
import GenerateEntitySidebar from '../GenerateEntitySidebar';
import AddDocumentSidebar from '../AddDocumentSidebar';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUser } from '../../context/UserContext';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import { exportToCSV } from '../../utils/exportToCSV';

interface RATabProps {
  projectId: string;
  onExport?: () => void;
  onNavigateToEntity?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

export const RATab: React.FC<RATabProps> = ({ projectId, onExport, onNavigateToEntity }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  
  const [risks, setRisks] = useState<RiskRecord[]>([]);
  const [selectedRisk, setSelectedRisk] = useState<RiskRecord | null>(null);
  const [showRiskEditor, setShowRiskEditor] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [rpnFilter, setRpnFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTransformEntitiesSidebar, setShowTransformEntitiesSidebar] = useState(false);
  const [showEntityGenerationSidebar, setShowEntityGenerationSidebar] = useState(false);
  const [showGenerateRiskSidebar, setShowGenerateRiskSidebar] = useState(false);
  const [selectedEntitiesForGeneration, setSelectedEntitiesForGeneration] = useState<Array<{
    id: string;
    type: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP';
    title: string;
  }>>([]);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);

  useEffect(() => {
    const loadRisks = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'risks');
        const snapshot = await getDocs(col);
        const loaded: RiskRecord[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.id || `RISK-${doc.id.substring(0, 8)}`,
            _firestoreId: doc.id
          } as RiskRecord & { _firestoreId: string };
        });
        setRisks(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading Risks:', err);
        }
        showToast(t('project_tab_risk_load_failed') || 'Failed to load Risks', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadRisks();
  }, [projectId, showToast, t]);

  const handleSaveRisk = async (risk: RiskRecord) => {
    try {
      const firestoreId = (risk as any)?._firestoreId;
      
      if (firestoreId) {
        // Update existing
        const docRef = doc(db, 'projects', projectId, 'risks', firestoreId);
        const { _firestoreId, ...dataToSave } = risk as any;
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        } as any);
      } else {
        // Create new
        await createRisk(projectId, risk);
      }
      
      // Reload risks from Firestore
      const col = collection(db, 'projects', projectId, 'risks');
      const snapshot = await getDocs(col);
      const loaded: RiskRecord[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: data.id || `RISK-${doc.id.substring(0, 8)}`,
          _firestoreId: doc.id
        } as RiskRecord & { _firestoreId: string };
      });
      setRisks(loaded);
      
      setShowRiskEditor(false);
      setSelectedRisk(null);
      showToast(t('project_tab_risk_saved') || 'Risk saved', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_risk_save_failed') || 'Failed to save risk', 'error');
    }
  };

  const handleAddRisk = () => {
    const newRisk: RiskRecord = {
      id: `RISK-${String(risks.length + 1).padStart(3, '0')}`,
      type: 'RISK',
      title: '',
      risk_statement: '',
      cause: '',
      consequence: '',
      likelihood: 3,
      severity: 3,
      detectability: 3,
      initial_risk_rating: 'Medium',
      residual_risk_rating: 'Medium',
      status: 'Draft',
      controls: { technical: [], procedural: [] },
      links: { urs_ids: [], fs_ids: [], test_ids: [], sop_ids: [] },
      meta: { owner: '', tags: [] },
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setSelectedRisk(newRisk);
    setShowRiskEditor(true);
  };

  const handleAiIdentifyRisks = () => {
    setShowGenerateRiskSidebar(true);
  };

  const extractRISKNumber = (id: string): number => {
    const match = id.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const handleDeleteRisk = (riskId: string) => {
    setRiskToDelete(riskId);
    setShowDeleteModal(true);
  };

  const confirmDeleteRisk = async () => {
    if (!riskToDelete) return;
    setIsDeleting(true);
    try {
      const risk = risks.find(r => r.id === riskToDelete);
      if (risk && (risk as any)._firestoreId) {
        const docRef = doc(db, 'projects', projectId, 'risks', (risk as any)._firestoreId);
        await deleteDoc(docRef);
      }
      setRisks(risks.filter(r => r.id !== riskToDelete));
      showToast(t('project_tab_risk_deleted') || 'Risk deleted', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_risk_delete_failed') || 'Failed to delete risk', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setRiskToDelete(null);
    }
  };

  const handleEntityClick = (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'SOP') => {
    onNavigateToEntity?.(entityId, entityType);
  };

  const handleAiGenerateFromRisk = () => {
    // Open sidebar with ALL Risks selected
    if (risks.length === 0) {
      showToast(t('project_tab_no_risks_to_generate') || 'No risks available. Please create a risk first.', 'error');
      return;
    }
    const allRisks = risks.map(risk => ({
      id: risk.id,
      type: 'RISK' as const,
      title: risk.title
    }));
    setSelectedEntitiesForGeneration(allRisks);
    setShowEntityGenerationSidebar(true);
  };

  const handleUploadRiskClick = () => {
    setShowAddDocumentSidebar(true);
  };

  // Filter and sort risks based on search and rating
  const filteredRisks = useMemo(() => {
    const filtered = risks.filter(risk => {
      const matchesSearch = !searchValue || 
        risk.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        risk.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        risk.risk_statement.toLowerCase().includes(searchValue.toLowerCase());
      const matchesRPN = !rpnFilter || 
        (rpnFilter === 'high' && risk.initial_risk_rating === 'High') ||
        (rpnFilter === 'medium' && risk.initial_risk_rating === 'Medium') ||
        (rpnFilter === 'low' && risk.initial_risk_rating === 'Low');
      return matchesSearch && matchesRPN;
    });
    
    return filtered.sort((a, b) => {
      const numA = extractRISKNumber(a.id);
      const numB = extractRISKNumber(b.id);
      return numA - numB;
    });
  }, [risks, searchValue, rpnFilter]);

  const tabs = [
    {
      label: t('project_tab_list') || 'Risks',
      queryValue: 'risks',
      content: (
        <div className={styles.risksSection}>
          {/* FilterBar */}
          <FilterBar
            filters={[
              {
                id: 'rpn',
                label: t('project_tab_rpn') || 'RPN',
                options: [
                  { value: '', label: t('project_tab_all') || 'All' },
                  { value: 'high', label: t('project_tab_high_risk') || 'High (≥100)' },
                  { value: 'medium', label: t('project_tab_medium_risk') || 'Medium (50-99)' },
                  { value: 'low', label: t('project_tab_low_risk') || 'Low (<50)' }
                ],
                value: rpnFilter,
                onChange: (value) => setRpnFilter(value)
              }
            ]}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t('project_tab_search_risks') || 'Search risks...'}
            showInviteButton={true}
            onAddUser={handleAddRisk}
            addButtonText={t('project_tab_add_risk') || 'Add Risk'}
          />

          {loading ? null : filteredRisks.length === 0 ? (
            <GlobalEmptyState
              buttonText={t('project_tab_add_risk') || 'Add Risk'}
              buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              onButtonClick={handleAddRisk}
            />
          ) : (
            <div className={styles.risksList}>
              {filteredRisks.map((risk) => {
                // Use Firestore document ID as key to avoid duplicate key warnings
                const firestoreId = (risk as any)?._firestoreId || risk.id;
                return (
                <div key={firestoreId} className={styles.riskItem}>
                  <RiskCard
                    risk={risk}
                    projectId={projectId}
                    onEdit={(risk) => {
                      setSelectedRisk(risk);
                      setShowRiskEditor(true);
                    }}
                    onDelete={handleDeleteRisk}
                    onViewMitigation={(risk) => {
                      showToast(t('project_tab_viewing_mitigation') || 'Viewing mitigation', 'info');
                    }}
                    onEntityClick={handleEntityClick}
                  />
                </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Add risk */}
          <SmallServiceCard
            id="add-risk"
            title={t('project_tab_add_risk') || 'Add Risk'}
            description={t('project_tab_add_risk_desc') || 'Create a new risk entry in the risk register'}
            fileTypeIcon=""
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
            buttonText={t('project_tab_add') || 'Add'}
            buttonOnClick={handleAddRisk}
          />

          {/* Read file with AI */}
          <SmallServiceCard
            id="read-file-with-ai"
            title={t('project_tab_read_file_with_ai') || 'Datei mit KI einlesen'}
            description={t('project_tab_read_file_with_ai_desc') || 'Importieren Sie Dateien um Risiken automatisch auslesen und erstellen zu lassen.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"}
            buttonText={t('project_tab_import') || 'Importieren'}
            buttonOnClick={handleUploadRiskClick}
            disabled={!user}
          />

          {/* AI Identify Risks */}
          <SmallServiceCard
            id="ai-identify-risks"
            title={t('project_tab_ai_identify_risks') || 'AI Identify Risks'}
            description={t('project_tab_ai_identify_risks_desc') || 'Use AI to automatically identify potential risks'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_identify') || 'Identify'}
            buttonOnClick={handleAiIdentifyRisks}
          />

          {/* Generate with AI from ALL Risks */}
          <SmallServiceCard
            id="ai-generate-from-all-risks"
            title={t('project_tab_ai_generate_from_all_risks') || 'Mit KI aus Risiko generieren'}
            description={t('project_tab_ai_generate_from_all_risks_desc') || 'Generieren Sie verbundene Entitäten (URS, Tests, Risiken, SOPs) mit KI wie aus den FS\'s'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generieren'}
            buttonOnClick={handleAiGenerateFromRisk}
          />

          {/* Transform Entities */}
          <SmallServiceCard
            id="transform-entities-risks"
            title={t('project_tab_transform_risks') || 'Transform Risk'}
            description={t('project_tab_transform_risks_desc') || 'Transform risks to replace content, translate, or rephrase.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_transform') || 'Transform'}
            buttonOnClick={() => setShowTransformEntitiesSidebar(true)}
          />

          {/* Export PDF */}
          <SmallServiceCard
            id="export-pdf-ra"
            title={t('project_tab_ra_export_pdf') || 'Als PDF exportieren'}
            description={t('project_tab_ra_export_pdf_desc') || 'Exportieren Sie alle Risiken gesammelt als ein PDF Dokument.'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export CSV */}
          <SmallServiceCard
            id="export-csv-ra"
            title={t('project_tab_ra_export_csv') || 'Als CSV exportieren'}
            description={t('project_tab_ra_export_csv_desc') || 'Exportieren Sie alle Risiken gesammelt als ein CSV Dokument.'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={() => {
              try {
                if (risks.length === 0) {
                  showToast(t('project_tab_no_risks_to_export') || 'No risks to export', 'warning');
                  return;
                }
                exportToCSV(risks, `RA_Export_${projectId}_${new Date().toISOString().split('T')[0]}`);
                showToast(t('project_tab_export_success') || 'Export successful', 'success');
              } catch (error) {
                console.error('Error exporting CSV:', error);
                showToast(t('project_tab_export_failed') || 'Export failed', 'error');
              }
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.raTab}>
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_ra') || 'RA (Risk Analysis)'}
        </h2>
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="risks" />

      {/* Entity Generation Sidebar */}
      <EntityGenerationSidebar
        isOpen={showEntityGenerationSidebar}
        onClose={() => {
          setShowEntityGenerationSidebar(false);
          setSelectedEntitiesForGeneration([]);
        }}
        projectId={projectId}
        selectedEntities={selectedEntitiesForGeneration}
        onSuccess={() => {
          // Reload risks to show any new entities
          const loadRisks = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'risks');
              const snapshot = await getDocs(col);
              const loaded: RiskRecord[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `RISK-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as RiskRecord & { _firestoreId: string };
              });
              setRisks(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading risks:', err);
              }
            }
          };
          loadRisks();
        }}
      />

      {/* Generate Risk Sidebar */}
      <GenerateEntitySidebar
        isOpen={showGenerateRiskSidebar}
        onClose={() => {
          setShowGenerateRiskSidebar(false);
        }}
        projectId={projectId}
        entityType="RISK"
        onSuccess={() => {
          // Reload risks to show generated entity
          const loadRisks = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'risks');
              const snapshot = await getDocs(col);
              const loaded: RiskRecord[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `RISK-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as RiskRecord & { _firestoreId: string };
              });
              setRisks(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading risks:', err);
              }
            }
          };
          loadRisks();
        }}
      />

      {/* Transform Entities Sidebar */}
      <TransformEntitiesSidebar
        isOpen={showTransformEntitiesSidebar}
        onClose={() => {
          setShowTransformEntitiesSidebar(false);
        }}
        projectId={projectId}
        onSuccess={() => {
          // Reload risks to show transformed entities
          const loadRisks = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'risks');
              const snapshot = await getDocs(col);
              const loaded: RiskRecord[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `RISK-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as RiskRecord & { _firestoreId: string };
              });
              setRisks(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading risks:', err);
              }
            }
          };
          loadRisks();
        }}
      />

      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_risk_title')?.replace('{id}', riskToDelete || '') || `Delete Risk ${riskToDelete}?`}
        paragraph={t('project_tab_delete_risk_paragraph') || 'Are you sure you want to delete this risk? This action cannot be undone.'}
        primaryLabel={t('global_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('global_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={confirmDeleteRisk}
        onSecondary={() => {
          setShowDeleteModal(false);
          setRiskToDelete(null);
        }}
        primaryDestructive
        loading={isDeleting}
      />

      {showRiskEditor && selectedRisk && (
        <RiskEditor
          projectId={projectId}
          risk={selectedRisk}
          onSave={async (risk) => {
            await handleSaveRisk(risk);
          }}
          onCancel={() => {
            setShowRiskEditor(false);
            setSelectedRisk(null);
          }}
          isNew={!risks.some(r => r.id === selectedRisk.id)}
        />
      )}

      {/* Add Document Sidebar */}
      <AddDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        projectId={projectId}
        defaultEntityTypes={['RISK']}
        onSuccess={() => {
          // Reload risks after document upload
          const loadRisks = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'risks');
              const snapshot = await getDocs(col);
              const loaded: RiskRecord[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `RISK-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as RiskRecord & { _firestoreId: string };
              });
              setRisks(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading risks:', err);
              }
            }
          };
          loadRisks();
        }}
      />
    </div>
  );
};

interface RiskEditorProps {
  projectId: string;
  risk: RiskRecord;
  onSave: (risk: RiskRecord) => Promise<void>;
  onCancel: () => void;
  isNew: boolean;
}

const RiskEditor: React.FC<RiskEditorProps> = ({ projectId, risk, onSave, onCancel, isNew }) => {
  const { t } = useLocale();
  const [editedRisk, setEditedRisk] = useState<RiskRecord>(risk);

  const parseComma = (value: string) => value.split(',').map(s => s.trim()).filter(Boolean);

  return (
    <GlobalSidebar isOpen={true} onClose={onCancel}>
      <div>
        <div className={styles.editorHeader}>
          <h3>
            {isNew
              ? (t('project_tab_risk_add') || 'Add Risk')
              : (t('project_tab_risk_edit') || 'Edit Risk')}
            <span className={styles.riskId}> {editedRisk.id}</span>
          </h3>
        </div>
        <div className={styles.editorContent}>
          <div className={styles.modalTabs}>
            <GlobalTabs
              tabs={[
                {
                  label: t('project_tab_risk_details') || 'Details',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalTextarea
                        label={t('project_tab_title') || 'Title'}
                        value={editedRisk.title}
                        onChange={(e) => setEditedRisk({ ...editedRisk, title: e.target.value })}
                        rows={2}
                      />
                      <GlobalTextarea
                        label={t('project_tab_risk_statement') || 'Risk Statement'}
                        value={editedRisk.risk_statement}
                        onChange={(e) => setEditedRisk({ ...editedRisk, risk_statement: e.target.value })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_cause') || 'Cause'}
                        value={editedRisk.cause}
                        onChange={(e) => setEditedRisk({ ...editedRisk, cause: e.target.value })}
                        rows={2}
                      />
                      <GlobalTextarea
                        label={t('project_tab_consequence') || 'Consequence'}
                        value={editedRisk.consequence}
                        onChange={(e) => setEditedRisk({ ...editedRisk, consequence: e.target.value })}
                        rows={2}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_risk_assessment_tab') || 'Assessment',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalDropdown
                        label={t('project_tab_severity') || 'Severity (1-5)'}
                        value={String(editedRisk.severity)}
                        onChange={(v) => setEditedRisk({ ...editedRisk, severity: parseInt(v) as any })}
                        options={[
                          { value: '1', label: '1' },
                          { value: '2', label: '2' },
                          { value: '3', label: '3' },
                          { value: '4', label: '4' },
                          { value: '5', label: '5' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_likelihood') || 'Likelihood (1-5)'}
                        value={String(editedRisk.likelihood)}
                        onChange={(v) => setEditedRisk({ ...editedRisk, likelihood: parseInt(v) as any })}
                        options={[
                          { value: '1', label: '1' },
                          { value: '2', label: '2' },
                          { value: '3', label: '3' },
                          { value: '4', label: '4' },
                          { value: '5', label: '5' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_detectability') || 'Detectability (1-5)'}
                        value={String(editedRisk.detectability ?? 3)}
                        onChange={(v) => setEditedRisk({ ...editedRisk, detectability: parseInt(v) as any })}
                        options={[
                          { value: '1', label: '1' },
                          { value: '2', label: '2' },
                          { value: '3', label: '3' },
                          { value: '4', label: '4' },
                          { value: '5', label: '5' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_initial_rating') || 'Initial Risk Rating'}
                        value={editedRisk.initial_risk_rating}
                        onChange={(v) => setEditedRisk({ ...editedRisk, initial_risk_rating: v as any })}
                        options={[
                          { value: 'Low', label: t('project_tab_rating_low') || 'Low' },
                          { value: 'Medium', label: t('project_tab_rating_medium') || 'Medium' },
                          { value: 'High', label: t('project_tab_rating_high') || 'High' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_residual_rating') || 'Residual Risk Rating'}
                        value={editedRisk.residual_risk_rating}
                        onChange={(v) => setEditedRisk({ ...editedRisk, residual_risk_rating: v as any })}
                        options={[
                          { value: 'Low', label: t('project_tab_rating_low') || 'Low' },
                          { value: 'Medium', label: t('project_tab_rating_medium') || 'Medium' },
                          { value: 'High', label: t('project_tab_rating_high') || 'High' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_status') || 'Status'}
                        value={editedRisk.status}
                        onChange={(v) => setEditedRisk({ ...editedRisk, status: v as any })}
                        options={[
                          { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                          { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                          { value: 'Approved', label: t('project_tab_status_approved') || 'Approved' },
                          { value: 'Retired', label: t('project_tab_status_rejected') || 'Retired' }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_risk_controls') || 'Controls',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalTextarea
                        label={t('project_tab_technical_controls') || 'Technical Controls (one per line)'}
                        value={(editedRisk.controls?.technical || []).join('\n')}
                        onChange={(e) => setEditedRisk({ ...editedRisk, controls: { ...(editedRisk.controls || {}), technical: parseComma(e.target.value.replace(/\n/g, ',')) } })}
                        rows={3}
                      />
                      <GlobalTextarea
                        label={t('project_tab_procedural_controls') || 'Procedural Controls (one per line)'}
                        value={(editedRisk.controls?.procedural || []).join('\n')}
                        onChange={(e) => setEditedRisk({ ...editedRisk, controls: { ...(editedRisk.controls || {}), procedural: parseComma(e.target.value.replace(/\n/g, ',')) } })}
                        rows={3}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_risk_links') || 'Connections',
                  content: (
                    <div className={styles.modalTabSection}>
                      <URSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_urs') || 'Linked URS'}
                        placeholder={t('project_tab_search_requirements') || 'Search URS...'}
                        selectedIds={editedRisk.links.urs_ids}
                        currentEntityId={editedRisk.id}
                        currentEntityType="RISK"
                        onChange={(ids: string[]) =>
                          setEditedRisk({
                            ...editedRisk,
                            links: { ...editedRisk.links, urs_ids: ids }
                          })
                        }
                      />
                      <FSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_fs') || 'Linked FS'}
                        placeholder={t('project_tab_search_sops') || 'Search FS...'}
                        selectedIds={editedRisk.links.fs_ids}
                        currentEntityId={editedRisk.id}
                        currentEntityType="RISK"
                        onChange={(ids: string[]) =>
                          setEditedRisk({
                            ...editedRisk,
                            links: { ...editedRisk.links, fs_ids: ids }
                          })
                        }
                      />
                      <TestAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_tests') || 'Linked TEST'}
                        placeholder={t('project_tab_search_tests') || 'Search Tests...'}
                        selectedIds={editedRisk.links.test_ids}
                        currentEntityId={editedRisk.id}
                        currentEntityType="RISK"
                        onChange={(ids: string[]) =>
                          setEditedRisk({
                            ...editedRisk,
                            links: { ...editedRisk.links, test_ids: ids }
                          })
                        }
                      />
                      <SOPAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_sops') || 'Linked SOP'}
                        placeholder={t('project_tab_search_sops') || 'Search SOPs...'}
                        selectedIds={editedRisk.links.sop_ids}
                        currentEntityId={editedRisk.id}
                        currentEntityType="RISK"
                        onChange={(ids: string[]) =>
                          setEditedRisk({
                            ...editedRisk,
                            links: { ...editedRisk.links, sop_ids: ids }
                          })
                        }
                      />
                    </div>
                  )
                },
              ]}
            />
          </div>
        </div>
        <div className={styles.editorActions}>
          <GlobalButton variant="secondary" onClick={onCancel}>
            {t('project_tab_cancel') || 'Cancel'}
          </GlobalButton>
          <GlobalButton onClick={async () => await onSave(editedRisk)}>
            {t('project_tab_save') || 'Save'}
          </GlobalButton>
        </div>
      </div>
    </GlobalSidebar>
  );
};

